/**
 * Page publique — Fiche détail d'une retraite avec tunnel de réservation.
 * URL : /retraites/:slug
 *
 * Sections :
 *  - Hero (titre, dates, lieu, prix "à partir de")
 *  - Description immersive
 *  - Lieu (nom + description + adresse)
 *  - Programme jour par jour
 *  - Formules & tarifs (chambres + option Day)
 *  - Formulaire de réservation → Stripe Checkout
 *  - Formulaire liste d'attente si complet
 *
 * Le stock est calculé côté back (`GET /api/retreats/:slug` renvoie `availability`).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, MapPin, BedDouble, Users, Utensils, Sparkles,
  Check, AlertCircle, Loader2, X, LogIn, User as UserIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AppContexts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_SITE_URL || "https://www.bilan2vie.fr";

export default function RetreatDetailPage() {
  const { slug } = useParams();
  const [retreat, setRetreat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null); // index dans gallery, ou null

  useEffect(() => {
    axios.get(`${API}/retreats/${slug}`)
      .then((r) => setRetreat(r.data))
      .catch(() => setError("Retraite introuvable"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0a0a1a] text-white/60"><Loader2 className="animate-spin mr-2" /> Chargement…</div>;
  }
  if (error || !retreat) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0a0a1a] text-white/60">{error || "Retraite introuvable"}</div>;
  }

  const avail = retreat.availability || {};
  const dates = formatDatesRange(retreat.start_date, retreat.end_date);
  const canonicalUrl = `${SITE_URL}/retraites/${slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: retreat.title,
    description: retreat.meta_description || retreat.description?.slice(0, 300),
    startDate: retreat.start_date,
    endDate: retreat.end_date,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    inLanguage: "fr-FR",
    isAccessibleForFree: false,
    keywords: [
      "retraite spirituelle", "séjour bien-être", "méditation",
      retreat.location?.city, retreat.location?.region,
      "Drôme", "Provence", "France",
    ].filter(Boolean).join(", "),
    location: {
      "@type": "Place",
      name: retreat.location?.name,
      address: {
        "@type": "PostalAddress",
        streetAddress: retreat.location?.address,
        addressLocality: retreat.location?.city,
        addressRegion: retreat.location?.region || "Drôme",
        addressCountry: retreat.location?.country || "FR",
        postalCode: retreat.location?.postal_code,
      },
    },
    organizer: {
      "@type": "Organization",
      name: "Bilan 2 Vie",
      url: SITE_URL,
      logo: `${SITE_URL}/favicon-96x96.png`,
    },
    performer: {
      "@type": "Person",
      name: "Guillaume — Bilan 2 Vie",
      jobTitle: "Praticien spirituel, médium, énergéticien",
      url: `${SITE_URL}/qui-suis-je`,
    },
    image: [
      retreat.cover_image,
      ...(retreat.gallery || []).slice(0, 3).map((g) => g.url || g),
    ].filter(Boolean),
    offers: (retreat.room_types || []).map((rt) => ({
      "@type": "Offer",
      name: rt.name,
      price: rt.price_single,
      priceCurrency: "EUR",
      url: canonicalUrl,
      validFrom: new Date().toISOString().slice(0, 10),
      availability: (avail.room_types_stock?.[rt.room_type_id] || 0) > 0
        ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
    })),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Séjours & Retraites", item: `${SITE_URL}/retraites` },
      { "@type": "ListItem", position: 3, name: retreat.title, item: canonicalUrl },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] to-[#1a1a2e] text-white pb-24">
      <Helmet>
        <title>{`${retreat.title} — ${dates} — Séjour spirituel ${retreat.location?.city || "Drôme"} | Bilan 2 Vie`}</title>
        <meta name="description" content={retreat.meta_description || retreat.subtitle || `Séjour spirituel ${retreat.title} du ${dates} — ${retreat.location?.name || "Drôme provençale"}. Méditation, soins énergétiques, ateliers. Réservation en ligne, paiement 3-4x via Klarna.`} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="geo.region" content="FR-26" />
        {retreat.location?.city && <meta name="geo.placename" content={retreat.location.city} />}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Bilan 2 Vie" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:title" content={`${retreat.title} — ${dates}`} />
        <meta property="og:description" content={retreat.subtitle || retreat.meta_description || ""} />
        <meta property="og:url" content={canonicalUrl} />
        {retreat.cover_image && <meta property="og:image" content={retreat.cover_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${retreat.title} — ${dates}`} />
        <meta name="twitter:description" content={retreat.subtitle || ""} />
        {retreat.cover_image && <meta name="twitter:image" content={retreat.cover_image} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-24 pb-12">
        {retreat.cover_image && (
          <div className="absolute inset-0 opacity-30">
            <img src={retreat.cover_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-[#0a0a1a]/80 to-[#0a0a1a]" />
          </div>
        )}
        <div className="relative max-w-4xl mx-auto px-6">
          <Badge className="mb-4 bg-[#F5D8A2]/20 text-[#F5D8A2] border-[#F5D8A2]/40">Retraite spirituelle</Badge>
          <h1 className="text-3xl sm:text-5xl font-light leading-tight mb-4" data-testid="retreat-h1">{retreat.title}</h1>
          {retreat.subtitle && <p className="text-lg text-white/70 mb-6">{retreat.subtitle}</p>}
          <div className="flex flex-wrap gap-4 text-sm text-white/70">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#F5D8A2]" />{dates}</span>
            {retreat.location?.name && (
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#F5D8A2]" />{retreat.location.name}{retreat.location.city ? `, ${retreat.location.city}` : ""}</span>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 space-y-16">
        {/* Description */}
        {retreat.description && (
          <section>
            <p className="whitespace-pre-line text-white/80 leading-relaxed text-lg">{retreat.description}</p>
          </section>
        )}

        {/* Lieu */}
        {retreat.location?.name && (
          <section>
            <h2 className="text-2xl font-light mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-[#F5D8A2]" /> Le lieu</h2>
            <div className="bg-white/5 border border-white/10 rounded-lg p-6">
              <h3 className="font-medium mb-2">{retreat.location.name}</h3>
              {retreat.location.description && <p className="text-white/70 mb-3 whitespace-pre-line">{retreat.location.description}</p>}
              {retreat.location.address && <p className="text-sm text-white/50">{retreat.location.address}, {retreat.location.city}</p>}
            </div>
          </section>
        )}

        {/* Galerie photos */}
        {(retreat.gallery && retreat.gallery.length > 0) && (
          <section>
            <h2 className="text-2xl font-light mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#F5D8A2]" /> En images</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="retreat-gallery">
              {retreat.gallery.map((img, idx) => {
                const url = typeof img === "string" ? img : img.url;
                const caption = typeof img === "object" ? (img.caption || "") : "";
                return (
                  <button
                    key={idx}
                    onClick={() => setLightbox(idx)}
                    className="aspect-square overflow-hidden rounded-lg group focus:outline-none focus:ring-2 focus:ring-[#F5D8A2] relative"
                    data-testid={`gallery-photo-${idx}`}
                  >
                    <img src={url} alt={caption || `Photo ${idx + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-white text-xs opacity-0 group-hover:opacity-100 transition text-left">
                        {caption}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Programme */}
        {retreat.program?.length > 0 && (
          <section>
            <h2 className="text-2xl font-light mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#F5D8A2]" /> Le programme</h2>
            <p className="text-sm text-white/50 mb-4 italic">Le programme peut légèrement évoluer selon les énergies du groupe.</p>
            <div className="space-y-3">
              {retreat.program.map((p, idx) => (
                <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-5">
                  <div className="text-xs uppercase tracking-wider text-[#F5D8A2] mb-1">Jour {p.day_index}</div>
                  {p.title && <h3 className="font-medium mb-1">{p.title}</h3>}
                  {p.description && <p className="text-white/70 text-sm whitespace-pre-line">{p.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Formules & tarifs — ancre `#reservation` utilisée par le CTA mobile
            pour scroller directement au module de résa depuis n'importe où sur la page. */}
        <section id="reservation" className="scroll-mt-24">
          <h2 className="text-2xl font-light mb-2 flex items-center gap-2"><BedDouble className="w-5 h-5 text-[#F5D8A2]" /> Formules & réservation</h2>
          <p className="text-sm text-white/60 mb-6">Choisis ta formule ci-dessous. Acompte 30% à la réservation (CB ou Klarna en 3-4 fois), solde à J-30.</p>
          <BookingModule retreat={retreat} />
        </section>

        {/* Politique annulation */}
        <section className="bg-white/5 border border-white/10 rounded-lg p-6 text-sm text-white/60">
          <h3 className="text-white/80 font-medium mb-2 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-[#F5D8A2]" /> Politique d'annulation</h3>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>Acompte de 30% <strong>non remboursable</strong> une fois la place bloquée.</li>
            <li>Entre J-45 et J-30 : l'acompte est perdu mais le solde te sera remboursé s'il a déjà été payé.</li>
            <li>À moins de J-30 : aucun remboursement possible, sauf si une personne de la liste d'attente peut te remplacer, ou si tu trouves toi-même quelqu'un.</li>
            <li>À défaut de règlement du solde à J-30, la réservation est automatiquement annulée et la chambre libérée.</li>
          </ul>
        </section>
      </div>

      {/* Lightbox */}
      {lightbox !== null && retreat.gallery && (() => {
        const currentImg = retreat.gallery[lightbox];
        const currentUrl = typeof currentImg === "string" ? currentImg : currentImg.url;
        const currentCaption = typeof currentImg === "object" ? (currentImg.caption || "") : "";
        return (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
          role="dialog"
        >
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Fermer"
          >
            <X className="w-8 h-8" />
          </button>
          {lightbox > 0 && (
            <button
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-4xl p-3 select-none"
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }}
              aria-label="Précédent"
            >‹</button>
          )}
          {lightbox < (retreat.gallery.length - 1) && (
            <button
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-4xl p-3 select-none"
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }}
              aria-label="Suivant"
            >›</button>
          )}
          <div className="flex flex-col items-center gap-4 max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
            <img
              src={currentUrl}
              alt={currentCaption || `Photo ${lightbox + 1}`}
              className="max-w-[90vw] max-h-[80vh] object-contain rounded"
            />
            {currentCaption && (
              <p className="text-white/80 text-sm italic max-w-2xl text-center">{currentCaption}</p>
            )}
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs tracking-widest">
            {lightbox + 1} / {retreat.gallery.length}
          </div>
        </div>
        );
      })()}
    </div>
  );
}

function BookingModule({ retreat }) {
  const { user, setAuthModalOpen } = useAuth();
  const [tab, setTab] = useState("room");
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [occupancy, setOccupancy] = useState("single");
  // Day participants : liste dynamique. Le 1er est le client principal (rempli via les coordonnées ci-dessous).
  const [dayParticipants, setDayParticipants] = useState([{ first_name: "", last_name: "" }]);
  const [paymentType, setPaymentType] = useState("deposit");
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [form, setForm] = useState({
    email: "", first_name: "", last_name: "", phone: "",
    companion_first_name: "", companion_last_name: "",
    dietary_restrictions: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Pré-remplissage automatique quand un utilisateur est connecté (ou vient de se connecter).
  // On garde le champ éditable si le client veut corriger avant de payer.
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        email: prev.email || user.email || "",
        first_name: prev.first_name || user.first_name || user.name?.split(" ")?.[0] || "",
        last_name: prev.last_name || user.last_name || user.name?.split(" ")?.slice(1).join(" ") || "",
        phone: prev.phone || user.phone || "",
      }));
    }
  }, [user]);

  const avail = retreat.availability || {};
  const rooms = retreat.room_types || [];
  const dayEnabled = retreat.day_pass_enabled && (avail.day_remaining || 0) > 0;

  const anyRoomLeft = useMemo(
    () => rooms.some((rt) => (avail.room_types_stock?.[rt.room_type_id] || 0) > 0),
    [rooms, avail]
  );

  // Auto-select first available room
  useEffect(() => {
    if (!selectedRoom && anyRoomLeft) {
      const first = rooms.find((rt) => (avail.room_types_stock?.[rt.room_type_id] || 0) > 0);
      if (first) setSelectedRoom(first.room_type_id);
    }
  }, [rooms, avail, anyRoomLeft, selectedRoom]);

  const selectedRoomObj = rooms.find((rt) => rt.room_type_id === selectedRoom);
  const dayPax = dayParticipants.length;
  const currentTotal = useMemo(() => {
    if (tab === "day") return (retreat.day_pass_price || 0) * dayPax;
    if (!selectedRoomObj) return 0;
    return occupancy === "double" ? (selectedRoomObj.price_double || 0) : (selectedRoomObj.price_single || 0);
  }, [tab, selectedRoomObj, occupancy, dayPax, retreat]);

  const depositPercent = retreat.deposit_percent || 30;
  const rawDeposit = currentTotal * depositPercent / 100;
  const depositAmount = Math.round(rawDeposit * 100) / 100;  // .toFixed(2) aligné avec le backend
  const dueNow = paymentType === "full" ? currentTotal : depositAmount;

  // Sync : le 1er participant Day est toujours le client principal (nom/prénom du form).
  useEffect(() => {
    if (tab === "day" && dayParticipants.length > 0) {
      const first = dayParticipants[0];
      if (first.first_name !== form.first_name || first.last_name !== form.last_name) {
        const updated = [...dayParticipants];
        updated[0] = { first_name: form.first_name || "", last_name: form.last_name || "" };
        setDayParticipants(updated);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.first_name, form.last_name, tab]);

  if (avail.retreat_full) {
    return <WaitlistForm retreat={retreat} />;
  }

  const addDayParticipant = () => {
    if (dayParticipants.length >= (avail.day_remaining || 1)) {
      toast.error(`Il reste ${avail.day_remaining} places disponibles`);
      return;
    }
    setDayParticipants([...dayParticipants, { first_name: "", last_name: "" }]);
  };
  const updateDayParticipant = (idx, key, value) => {
    const updated = [...dayParticipants];
    updated[idx] = { ...updated[idx], [key]: value };
    setDayParticipants(updated);
  };
  const removeDayParticipant = (idx) => {
    if (idx === 0) return; // le 1er = client principal, non supprimable
    setDayParticipants(dayParticipants.filter((_, i) => i !== idx));
  };

  const handleBook = async () => {
    // Auth-first : le bouton n'appelle handleBook que si user est connecté (voir onClick),
    // mais on garde une garde de sécurité au cas où.
    if (!user) {
      setAuthModalOpen(true);
      return;
    }
    // Validations
    if (!form.email || !form.first_name || !form.last_name) {
      toast.error("Merci de renseigner ton nom, prénom et email.");
      return;
    }
    if (tab === "room" && !selectedRoom) {
      toast.error("Choisis une chambre.");
      return;
    }
    if (tab === "room" && occupancy === "double" && (!form.companion_first_name.trim() || !form.companion_last_name.trim())) {
      toast.error("Merci d'indiquer le nom et prénom de l'accompagnant·e.");
      return;
    }
    if (tab === "day") {
      for (let i = 0; i < dayParticipants.length; i++) {
        const p = dayParticipants[i];
        if (!p.first_name.trim() || !p.last_name.trim()) {
          toast.error(`Nom et prénom requis pour le participant ${i + 1}`);
          return;
        }
      }
    }
    // Consentement CGV obligatoire — protection juridique.
    if (!cgvAccepted) {
      toast.error("Merci d'accepter les Conditions Générales de Vente pour valider ta réservation.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        booking_type: tab,
        room_type_id: tab === "room" ? selectedRoom : null,
        occupancy: tab === "room" ? occupancy : "single",
        day_participants: tab === "day" ? dayParticipants : [],
        payment_type: paymentType,
        cgv_accepted: true,
        ...form,
      };
      const res = await axios.post(`${API}/retreats/${retreat.slug}/book`, payload, { withCredentials: true });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la réservation. Réessaie.");
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-6">
      {/* Badge places restantes */}
      <SpotsRemainingBadge avail={avail} />

      {/* Tabs formule */}
      <div className="flex gap-2 mb-6 border-b border-white/10">
        <button
          data-testid="tab-room"
          onClick={() => setTab("room")}
          disabled={!anyRoomLeft}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition ${tab === "room" ? "border-[#F5D8A2] text-[#F5D8A2]" : "border-transparent text-white/60 hover:text-white"} disabled:opacity-30 disabled:cursor-not-allowed`}
        >
          Formule Immersion (avec hébergement)
        </button>
        {dayEnabled && (
          <button
            data-testid="tab-day"
            onClick={() => setTab("day")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition ${tab === "day" ? "border-[#F5D8A2] text-[#F5D8A2]" : "border-transparent text-white/60 hover:text-white"}`}
          >
            Formule Day (sans hébergement)
          </button>
        )}
      </div>

      {tab === "room" && (
        <div className="space-y-6">
          {/* Choix chambre */}
          <div className="space-y-3">
            {rooms.map((rt) => {
              const stock = avail.room_types_stock?.[rt.room_type_id] || 0;
              const disabled = stock <= 0;
              const active = selectedRoom === rt.room_type_id;
              return (
                <button
                  key={rt.room_type_id}
                  data-testid={`room-${rt.room_type_id}`}
                  disabled={disabled}
                  onClick={() => setSelectedRoom(rt.room_type_id)}
                  className={`w-full text-left border rounded-lg p-4 transition ${active ? "border-[#F5D8A2] bg-[#F5D8A2]/5" : "border-white/10 bg-white/5 hover:border-white/20"} disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium">{rt.name}</div>
                      {rt.description && <div className="text-sm text-white/60 mt-1">{rt.description}</div>}
                    </div>
                    <div className="text-right text-sm shrink-0">
                      <div className="text-white/70">Simple : <strong className="text-[#F5D8A2]">{rt.price_single}€</strong></div>
                      <div className="text-white/70">Double : <strong className="text-[#F5D8A2]">{rt.price_double}€</strong></div>
                      <div className={`text-xs mt-1 ${disabled ? "text-red-400" : "text-white/40"}`}>
                        {disabled ? "Complet" : `${stock} dispo`}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Occupation */}
          {selectedRoomObj && (
            <div>
              <label className="text-sm text-white/60 block mb-2">Occupation</label>
              <div className="flex gap-2">
                {selectedRoomObj.allow_single && (
                  <button
                    data-testid="occ-single"
                    onClick={() => setOccupancy("single")}
                    className={`flex-1 py-3 px-4 rounded-lg border text-sm transition ${occupancy === "single" ? "border-[#F5D8A2] bg-[#F5D8A2]/10 text-[#F5D8A2]" : "border-white/10 text-white/70 hover:border-white/20"}`}
                  >
                    Simple ({selectedRoomObj.price_single}€)
                  </button>
                )}
                {selectedRoomObj.allow_double && (
                  <button
                    data-testid="occ-double"
                    onClick={() => setOccupancy("double")}
                    className={`flex-1 py-3 px-4 rounded-lg border text-sm transition ${occupancy === "double" ? "border-[#F5D8A2] bg-[#F5D8A2]/10 text-[#F5D8A2]" : "border-white/10 text-white/70 hover:border-white/20"}`}
                  >
                    Double ({selectedRoomObj.price_double}€ pour 2)
                  </button>
                )}
              </div>
            </div>
          )}

          {occupancy === "double" && (
            <div>
              <label className="text-sm text-white/60 block mb-2">
                Accompagnant·e <span className="text-red-400">*</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input data-testid="companion-first-name" placeholder="Prénom accompagnant·e" value={form.companion_first_name} onChange={(e) => setForm({ ...form, companion_first_name: e.target.value })} required />
                <Input data-testid="companion-last-name" placeholder="Nom accompagnant·e" value={form.companion_last_name} onChange={(e) => setForm({ ...form, companion_last_name: e.target.value })} required />
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "day" && dayEnabled && (
        <div className="space-y-4">
          <p className="text-sm text-white/60">{retreat.day_pass_description}</p>
          <div className="text-sm text-white/70">Tarif : <strong className="text-[#F5D8A2]">{retreat.day_pass_price}€ / personne</strong></div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-white/80 font-medium">Participants ({dayParticipants.length})</label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addDayParticipant}
                disabled={dayParticipants.length >= (avail.day_remaining || 1)}
                className="border-[#F5D8A2]/30 text-[#F5D8A2] hover:bg-[#F5D8A2]/10"
                data-testid="day-add-participant"
              >
                <Users className="w-4 h-4 mr-1" /> Ajouter un participant
              </Button>
            </div>
            <div className="space-y-2">
              {dayParticipants.map((p, idx) => (
                <div key={idx} className="grid sm:grid-cols-[auto_1fr_1fr_auto] gap-2 items-center bg-white/5 border border-white/10 rounded p-2" data-testid={`day-participant-${idx}`}>
                  <span className="text-xs text-[#F5D8A2] px-2 shrink-0">#{idx + 1}{idx === 0 && " (toi)"}</span>
                  <Input placeholder="Prénom" value={p.first_name} onChange={(e) => updateDayParticipant(idx, "first_name", e.target.value)} disabled={idx === 0} className="text-sm" />
                  <Input placeholder="Nom" value={p.last_name} onChange={(e) => updateDayParticipant(idx, "last_name", e.target.value)} disabled={idx === 0} className="text-sm" />
                  {idx > 0 && (
                    <button
                      type="button"
                      onClick={() => removeDayParticipant(idx)}
                      className="text-red-400 hover:bg-red-500/10 p-1 rounded"
                      data-testid={`day-remove-${idx}`}
                      aria-label="Retirer ce participant"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="text-xs text-white/40 mt-2">
              {avail.day_remaining} places externes restantes pour ce séjour.
            </div>
          </div>
        </div>
      )}

      {/* Coordonnées — auth-first : on encourage la création de compte pour retrouver
          ses réservations, ses reçus Stripe et pouvoir payer le solde plus tard. */}
      <div className="mt-6 space-y-3">
        {!user ? (
          <div className="bg-[#F5D8A2]/5 border border-[#F5D8A2]/20 rounded-lg p-5 text-center">
            <UserIcon className="w-8 h-8 text-[#F5D8A2] mx-auto mb-3" />
            <h3 className="font-medium text-white mb-1">Connecte-toi pour réserver</h3>
            <p className="text-sm text-white/60 mb-4 max-w-md mx-auto">
              Pour retrouver ta résa, télécharger tes reçus Stripe et payer le solde à J-30 sans ressaisir tes infos, il te faut un compte.
            </p>
            <Button
              type="button"
              onClick={() => setAuthModalOpen(true)}
              className="bg-[#F5D8A2] text-[#0a0a1a] hover:bg-[#F5D8A2]/90 font-semibold px-6"
              data-testid="booking-open-auth"
            >
              <LogIn className="w-4 h-4 mr-2" /> Se connecter ou créer un compte
            </Button>
            <p className="text-xs text-white/40 mt-3">
              Gratuit et en 30 secondes — création par email et mot de passe.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-white/80">Tes coordonnées</h3>
              <span className="text-xs text-white/40 flex items-center gap-1">
                <Check className="w-3 h-3 text-green-400" /> Connecté·e : {user.email}
              </span>
            </div>
            <p className="text-xs text-white/50">Pré-rempli depuis ton compte. Tu peux modifier avant de payer.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="book-first-name" placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <Input data-testid="book-last-name" placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="book-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="book-phone" placeholder="Téléphone (optionnel)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">
                Allergies alimentaires ou régime particulier (facultatif)
              </label>
              <textarea
                data-testid="book-dietary"
                rows={2}
                placeholder="Ex : végétarien·ne, allergie aux noix, sans gluten, sans lactose…"
                value={form.dietary_restrictions}
                onChange={(e) => setForm({ ...form, dietary_restrictions: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-md p-2 text-sm"
              />
            </div>
          </>
        )}
      </div>

      {/* Paiement */}
      <div className="mt-6">
        <h3 className="text-sm font-medium text-white/80 mb-2">Mode de paiement</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <button
            data-testid="pay-deposit"
            onClick={() => setPaymentType("deposit")}
            className={`text-left py-3 px-4 rounded-lg border transition ${paymentType === "deposit" ? "border-[#F5D8A2] bg-[#F5D8A2]/10" : "border-white/10 hover:border-white/20"}`}
          >
            <div className="text-sm font-medium">Acompte 30% aujourd'hui</div>
            <div className="text-xs text-white/50">Solde 70% à régler avant J-30 (email de rappel envoyé à J-45)</div>
          </button>
          <button
            data-testid="pay-full"
            onClick={() => setPaymentType("full")}
            className={`text-left py-3 px-4 rounded-lg border transition ${paymentType === "full" ? "border-[#F5D8A2] bg-[#F5D8A2]/10" : "border-white/10 hover:border-white/20"}`}
          >
            <div className="text-sm font-medium">Paiement intégral</div>
            <div className="text-xs text-white/50">Tout régler maintenant, plus rien à faire ensuite</div>
          </button>
        </div>
        <p className="text-xs text-white/40 mt-2">💳 CB ou Klarna (paiement en 3-4 fois) via Stripe.</p>
      </div>

      {/* Récap total */}
      <div className="mt-6 pt-6 border-t border-white/10 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-white/60">Total</span><strong className="text-white">{currentTotal.toFixed(2)}€</strong></div>
        {paymentType === "deposit" && (
          <>
            <div className="flex justify-between"><span className="text-white/60">À régler aujourd'hui (acompte 30%)</span><strong className="text-[#F5D8A2]">{dueNow.toFixed(2)}€</strong></div>
            <div className="flex justify-between text-white/50"><span>Solde à J-30</span><span>{(currentTotal - dueNow).toFixed(2)}€</span></div>
          </>
        )}
        {paymentType === "full" && (
          <div className="flex justify-between"><span className="text-white/60">À régler aujourd'hui</span><strong className="text-[#F5D8A2]">{currentTotal.toFixed(2)}€</strong></div>
        )}
      </div>

      {/* Consentement CGV — obligatoire uniquement pour l'utilisateur connecté prêt à payer.
          Le lien ouvre les CGV dans un nouvel onglet vers la section Séjours pour ne pas perdre
          la saisie en cours. */}
      {user && (
        <label className="mt-6 flex items-start gap-3 cursor-pointer text-xs text-white/70 leading-relaxed" data-testid="cgv-consent-block">
          <input
            type="checkbox"
            checked={cgvAccepted}
            onChange={(e) => setCgvAccepted(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#F5D8A2] shrink-0 cursor-pointer"
            data-testid="cgv-checkbox"
          />
          <span>
            J'ai lu et j'accepte les{" "}
            <a
              href="/cgv#sejours-retraites"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F5D8A2] underline hover:no-underline"
              data-testid="cgv-link"
            >
              Conditions Générales de Vente
            </a>
            {" "}(section « Séjours & Retraites »), incluant la politique
            d'acompte non remboursable et les modalités d'annulation.
          </span>
        </label>
      )}

      <Button
        data-testid="submit-booking"
        onClick={user ? handleBook : () => setAuthModalOpen(true)}
        disabled={submitting || currentTotal <= 0 || (!!user && !cgvAccepted)}
        className="w-full mt-4 bg-[#F5D8A2] text-[#0a0a1a] hover:bg-[#F5D8A2]/90 font-semibold py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirection…</>
        ) : !user ? (
          <><LogIn className="w-4 h-4 mr-2" /> Se connecter pour réserver</>
        ) : !cgvAccepted ? (
          "Accepte les CGV pour continuer"
        ) : (
          `Réserver ma place — ${dueNow.toFixed(2)}€`
        )}
      </Button>
      <p className="text-xs text-white/40 text-center mt-2">
        {user ? "Tu seras redirigé·e vers Stripe pour finaliser en toute sécurité." : "Compte obligatoire pour réserver — nous en avons besoin pour t'envoyer les infos pratiques, ton reçu et gérer le solde."}
      </p>
    </div>
  );
}

/**
 * Badge "il reste X places" — devient orange puis rouge quand le stock baisse.
 * Seuils : > 50% capacité restante = pas affiché, 25-50% = jaune, < 25% = rouge urgent.
 */
function SpotsRemainingBadge({ avail }) {
  const total = avail.total_capacity || 0;
  const remaining = avail.spots_remaining ?? total;
  if (!total || remaining <= 0 || remaining >= total * 0.5) return null;
  const isUrgent = remaining <= total * 0.25;
  return (
    <div
      data-testid="spots-remaining-badge"
      className={`mb-4 rounded-lg px-4 py-3 flex items-center gap-3 text-sm font-medium ${
        isUrgent
          ? "bg-red-500/15 border border-red-500/40 text-red-200"
          : "bg-yellow-500/10 border border-yellow-500/30 text-yellow-200"
      }`}
    >
      <span className="text-xl">{isUrgent ? "🔥" : "⚡"}</span>
      <span>
        {isUrgent
          ? `Plus que ${remaining} place${remaining > 1 ? "s" : ""} disponible${remaining > 1 ? "s" : ""} !`
          : `Il reste ${remaining} places disponibles`}
      </span>
    </div>
  );
}

function WaitlistForm({ retreat }) {
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", phone: "", preferred_occupancy: "single", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div className="bg-[#F5D8A2]/10 border border-[#F5D8A2]/30 rounded-lg p-8 text-center">
        <Check className="w-10 h-10 text-[#F5D8A2] mx-auto mb-3" />
        <h3 className="text-lg font-medium mb-1">Tu es inscrit·e sur la liste d'attente</h3>
        <p className="text-sm text-white/60">Dès qu'une place se libère, nous t'envoyons un email avec un lien de réservation prioritaire.</p>
      </div>
    );
  }

  const submit = async () => {
    if (!form.email || !form.first_name) { toast.error("Nom, prénom et email requis"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/retreats/${retreat.slug}/waitlist`, form);
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-6">
      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 mb-3">Retraite complète</Badge>
      <h3 className="text-xl font-medium mb-2">Rejoindre la liste d'attente</h3>
      <p className="text-sm text-white/60 mb-6">Si une place se libère (ex : désistement à J-30), tu seras averti·e par email par ordre chronologique d'inscription.</p>
      <div className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <Input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="wl-first" />
          <Input placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="wl-last" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="wl-email" />
          <Input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="wl-phone" />
        </div>
        <textarea
          placeholder="Préférence de chambre / notes (optionnel)"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={3}
          className="w-full bg-white/5 border border-white/10 rounded-md p-3 text-sm"
          data-testid="wl-notes"
        />
        <Button onClick={submit} disabled={submitting} className="w-full bg-[#F5D8A2] text-[#0a0a1a] hover:bg-[#F5D8A2]/90 font-semibold" data-testid="wl-submit">
          {submitting ? "Envoi…" : "M'inscrire sur la liste d'attente"}
        </Button>
      </div>
    </div>
  );
}

function formatDatesRange(start, end) {
  try {
    const s = new Date(start + "T00:00:00");
    const e = new Date(end + "T00:00:00");
    const opts = { day: "2-digit", month: "long", year: "numeric" };
    return `Du ${s.toLocaleDateString("fr-FR", opts)} au ${e.toLocaleDateString("fr-FR", opts)}`;
  } catch { return `${start} → ${end}`; }
}
